import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Collapse,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { AxiosError } from "axios";
import { NJIM, glassCardSx, pageShellSx, primaryBtnSx } from "../../../theme/njimTheme";
import { useTranslation } from "react-i18next";
import salesRepEnrolmentService from "../../../services/SalesRepEnrolmentService";
import loginService from "../../../services/LoginServices";
import {
  RepCustomerSummary,
  SalesRepProfile,
  SalesRepSyncResult,
  SalesRepSyncStatus,
} from "../../../types/salesRepEnrolment";
import { showToast } from "../../component/common/toastNotification";
import CustomInput from "../../component/common/inputField";
import { formatCodAmount } from "../../../utils/codUtils";
import {
  identityPasswordPolicyMessage,
  localizeEnrolmentRole,
  localizeEnrolmentSyncDescription,
  localizeEnrolmentSyncStatus,
} from "../../../helper/enrolmentI18n";

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const axiosErr = error as AxiosError;
  const data = axiosErr.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (Array.isArray(data) && data.length > 0) return data.map(String).join(". ");
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.status === "string" && obj.status.trim()) return obj.status.trim();
    if (typeof obj.message === "string") return obj.message;
    if (Array.isArray(obj.errors)) return obj.errors.map(String).join(". ");
  }
  return fallback;
};

const isSyncedStatus = (status?: string) =>
  status === "Synced" || status === "In System (Synced)";

const SalesRepEnrolment: React.FC = () => {
  const { t } = useTranslation();
  const [repCodeSearch, setRepCodeSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [profile, setProfile] = useState<SalesRepProfile | null>(null);
  const [syncStatus, setSyncStatus] = useState<SalesRepSyncStatus | null>(null);
  const [syncResult, setSyncResult] = useState<SalesRepSyncResult | null>(null);
  const [notFound, setNotFound] = useState(false);

  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetLoginUsername, setResetLoginUsername] = useState("");
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetPwConfirm, setShowResetPwConfirm] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [showManualRegister, setShowManualRegister] = useState(false);
  const [userName, setUserName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);

  const portalUsername =
    syncResult?.userName ||
    syncStatus?.userName ||
    profile?.portalAccount.userName ||
    resetLoginUsername ||
    profile?.salesRepCode ||
    "";

  const isSynced =
    isSyncedStatus(syncStatus?.status) || Boolean(profile?.portalAccount.exists);

  const resetPolicyError = resetPassword ? identityPasswordPolicyMessage(resetPassword, t) : null;
  const resetPasswordsMatch =
    Boolean(resetPassword) &&
    Boolean(resetPasswordConfirm) &&
    resetPassword === resetPasswordConfirm;
  const canResetPassword =
    Boolean(portalUsername.trim()) &&
    resetPasswordsMatch &&
    !resetPolicyError &&
    !resetLoading;

  const passwordPolicyError = password ? identityPasswordPolicyMessage(password, t) : null;
  const passwordsMatch =
    Boolean(password) && Boolean(passwordConfirm) && password === passwordConfirm;
  const canRegister =
    Boolean(profile?.salesRepCode) &&
    Boolean(userName.trim()) &&
    Boolean(firstName.trim()) &&
    Boolean(lastName.trim()) &&
    passwordsMatch &&
    !passwordPolicyError &&
    !registerLoading;

  const loadRep = async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) return;

    setSearchLoading(true);
    setNotFound(false);
    setProfile(null);
    setSyncStatus(null);
    setSyncResult(null);

    try {
      const [profileData, statusData] = await Promise.all([
        salesRepEnrolmentService.getProfile(trimmed),
        salesRepEnrolmentService.getSyncStatus(trimmed),
      ]);

      setProfile(profileData);
      setSyncStatus(statusData);
      setResetLoginUsername(
        statusData.userName || profileData.portalAccount.userName || profileData.salesRepCode
      );

      const nameParts = profileData.salesRepName.trim().split(/\s+/);
      setFirstName(nameParts[0] ?? "");
      setLastName(nameParts.slice(1).join(" "));

      showToast(
        t("salesRepEnrolment.repLoaded", { defaultValue: "Sales rep details loaded." }),
        "success"
      );
    } catch (error) {
      const axiosErr = error as AxiosError;
      if (axiosErr.response?.status === 404) {
        setNotFound(true);
      }
      showToast(
        extractApiErrorMessage(
          error,
          t("salesRepEnrolment.repNotFound", { defaultValue: "Sales rep not found." })
        ),
        "error"
      );
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearch = () => {
    if (!repCodeSearch.trim()) {
      showToast(
        t("salesRepEnrolment.enterRepCode", { defaultValue: "Enter a sales rep code." }),
        "error"
      );
      return;
    }
    loadRep(repCodeSearch);
  };

  const handleSync = async () => {
    if (!profile?.salesRepCode) return;
    try {
      setSyncLoading(true);
      const result = await salesRepEnrolmentService.syncRepNormalized(profile.salesRepCode);
      setSyncResult(result);
      setSyncStatus({ status: result.status, description: result.description, userName: result.userName });
      if (result.userName) setResetLoginUsername(result.userName);

      setProfile((prev) =>
        prev
          ? {
              ...prev,
              portalAccount: {
                exists: true,
                userName: result.userName || prev.portalAccount.userName,
                mustChangePassword: result.mustChangePassword ?? prev.portalAccount.mustChangePassword,
              },
            }
          : prev
      );

      showToast(
        t("salesRepEnrolment.syncSuccess", { defaultValue: "Sales rep portal account synced." }),
        "success"
      );

      await loadRep(profile.salesRepCode);
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("salesRepEnrolment.syncFailed", { defaultValue: "Failed to sync sales rep account." })
        ),
        "error"
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!canResetPassword) return;
    try {
      setResetLoading(true);
      await loginService.resetUserPassword(portalUsername.trim(), resetPassword);
      showToast(
        t("salesRepEnrolment.passwordResetSuccess", {
          user: portalUsername,
        }),
        "success"
      );
      setResetPassword("");
      setResetPasswordConfirm("");
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("salesRepEnrolment.passwordResetFailed", {
            defaultValue: "Failed to reset password. Sync the rep account first.",
          })
        ),
        "error"
      );
    } finally {
      setResetLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!canRegister || !profile) return;
    try {
      setRegisterLoading(true);
      await salesRepEnrolmentService.registerSalesRep({
        userName: userName.trim(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        salesRepCode: profile.salesRepCode,
      });
      setResetLoginUsername(userName.trim());
      showToast(
        t("salesRepEnrolment.accountCreated", {
          defaultValue: "Sales rep account created successfully.",
        }),
        "success"
      );
      await loadRep(profile.salesRepCode);
    } catch (error) {
      showToast(
        extractApiErrorMessage(
          error,
          t("salesRepEnrolment.registerFailed", {
            defaultValue: "Failed to create sales rep account.",
          })
        ),
        "error"
      );
    } finally {
      setRegisterLoading(false);
    }
  };

  const renderCustomerTable = (rows: RepCustomerSummary[], title: string, hint: string) => (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary, fontSize: "14px" }}>{title}</Typography>
        <Chip size="small" label={rows.length} sx={{ height: 22, fontWeight: 700 }} />
      </Stack>
      <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, mb: 1 }}>{hint}</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: "13px", color: NJIM.textSecondary, fontStyle: "italic" }}>
          {t("salesRepEnrolment.noCustomersInRole", { defaultValue: "No customers assigned in this role." })}
        </Typography>
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: "1px solid rgba(148,163,184,0.22)", maxHeight: 280 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, bgcolor: "rgba(241,245,249,0.95)" }}>
                  {t("salesRepEnrolment.customerCode", { defaultValue: "Customer Code" })}
                </TableCell>
                <TableCell sx={{ fontWeight: 700, bgcolor: "rgba(241,245,249,0.95)" }}>
                  {t("salesRepEnrolment.customerName", { defaultValue: "Customer Name" })}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.customerCode}>
                  <TableCell>{row.customerCode}</TableCell>
                  <TableCell>{row.customerName}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  return (
    <Box sx={pageShellSx} py={3}>
      <Box sx={{ maxWidth: 1180, mx: "auto", px: { xs: 1, sm: 2, md: 0 } }}>
        <Typography sx={{ fontWeight: 800, fontSize: "1.5rem", color: NJIM.textPrimary, mb: 0.5 }}>
          {t("salesRepEnrolment.title", { defaultValue: "Sales Rep Enrolment" })}
        </Typography>
        <Typography sx={{ color: NJIM.textSecondary, mb: 3 }}>
          {t("salesRepEnrolment.subtitle", {
            defaultValue: "Search by Sage rep code, sync portal accounts, and review assigned customers.",
          })}
        </Typography>

        <Card sx={glassCardSx}>
          <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "12px",
                display: "grid",
                placeItems: "center",
                background: "rgba(10,61,120,0.12)",
                border: "1px solid rgba(10,61,120,0.16)",
                color: NJIM.ink,
              }}
            >
              <PersonOutlineIcon sx={{ fontSize: 20 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
              {t("salesRepEnrolment.searchRep", { defaultValue: "Search Sales Rep" })}
            </Typography>
          </Stack>

          <Stack
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "flex-end" }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomInput
                name="repCodeSearch"
                label={t("salesRepEnrolment.repCodeLabel", { defaultValue: "Sales Rep Code" })}
                placeholder={t("salesRepEnrolment.repCodePlaceholder", { defaultValue: "e.g. 1028" })}
                value={repCodeSearch}
                onChange={(e) => setRepCodeSearch(e.target.value)}
                disabled={searchLoading}
                marginBottom="0px"
              />
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={searchLoading || !repCodeSearch.trim()}
              startIcon={searchLoading ? <CircularProgress size={16} sx={{ color: NJIM.white }} /> : <SearchIcon />}
              sx={{
                ...primaryBtnSx,
                minWidth: 140,
              }}
            >
              {t("salesRepEnrolment.searchBtn", { defaultValue: "Search" })}
            </Button>
          </Stack>

          {notFound && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "rgba(239,68,68,0.06)",
                border: "1px solid rgba(239,68,68,0.22)",
              }}
            >
              <Typography sx={{ color: NJIM.danger, fontWeight: 700, fontSize: "14px" }}>
                {t("salesRepEnrolment.repNotFoundTitle", { defaultValue: "Rep code not found in Sage" })}
              </Typography>
            </Box>
          )}
        </Card>

        {profile && (
          <>
            {syncResult?.tempPassword && (
              <Card
                sx={{
                  ...glassCardSx,
                  background: "linear-gradient(135deg, rgba(34,197,94,0.10), rgba(255,255,255,0.62))",
                  border: "1px solid rgba(34,197,94,0.18)",
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <CheckCircleOutlinedIcon sx={{ color: NJIM.success, mt: 0.3 }} />
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {t("salesRepEnrolment.syncCredentials", {
                      defaultValue: "Portal account created. Username: {{user}} — Temporary password: {{pw}}",
                      user: syncResult.userName || profile.salesRepCode,
                      pw: syncResult.tempPassword,
                    })}
                  </Typography>
                </Stack>
              </Card>
            )}

            <Card sx={glassCardSx}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
                <Box
                  sx={{
                    width: 45,
                    height: 45,
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    bgcolor: isSynced ? NJIM.mintWash : "rgba(245,158,11,0.14)",
                    color: isSynced ? NJIM.ink : NJIM.warning,
                    border: "1px solid rgba(148,163,184,0.22)",
                  }}
                >
                  {isSynced ? (
                    <CheckCircleOutlinedIcon />
                  ) : syncStatus?.status === "Not found" ? (
                    <CancelOutlinedIcon />
                  ) : (
                    <ReportProblemOutlinedIcon />
                  )}
                </Box>
                <Box flex={1}>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                    {t("salesRepEnrolment.syncStatus", { defaultValue: "Portal Sync Status" })}{" "}
                    <Chip
                      label={localizeEnrolmentSyncStatus(syncStatus?.status, t)}
                      size="small"
                      sx={{ marginInlineStart: 1, fontWeight: 800, bgcolor: NJIM.mintWash, color: NJIM.ink }}
                    />
                  </Typography>
                  <Typography color={NJIM.textSecondary} mt={0.75}>
                    {localizeEnrolmentSyncDescription(syncStatus?.description, t)}
                  </Typography>
                  {isSynced && portalUsername && (
                    <Typography color={NJIM.textSecondary} mt={0.5} sx={{ fontSize: "13px" }}>
                      {t("salesRepEnrolment.portalLogin", { defaultValue: "Portal login:" })}{" "}
                      <strong>{portalUsername}</strong>
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="contained"
                  startIcon={syncLoading ? <CircularProgress size={16} sx={{ color: NJIM.white }} /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={syncLoading}
                  sx={{
                    ...primaryBtnSx,
                    whiteSpace: "nowrap",
                  }}
                >
                  {isSynced
                    ? t("salesRepEnrolment.resyncBtn", { defaultValue: "Re-sync Account" })
                    : t("salesRepEnrolment.syncBtn", { defaultValue: "Sync & Create Account" })}
                </Button>
              </Stack>
            </Card>

            <Card sx={glassCardSx}>
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "12px",
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(34,197,94,0.12)",
                    border: "1px solid rgba(34,197,94,0.18)",
                    color: NJIM.success,
                  }}
                >
                  <GroupOutlinedIcon sx={{ fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                  {t("salesRepEnrolment.repDetails", { defaultValue: "Rep Details (from Sage)" })}
                </Typography>
              </Stack>

              <Grid container spacing={2}>
                {[
                  [t("salesRepEnrolment.repCode"), profile.salesRepCode],
                  [t("salesRepEnrolment.repName"), profile.salesRepName || "—"],
                  [t("salesRepEnrolment.repRole"), localizeEnrolmentRole(profile.repRole, t)],
                  [t("salesRepEnrolment.managerCode"), profile.salesManagerCode || "—"],
                  [t("salesRepEnrolment.managerName"), profile.salesManagerName || "—"],
                ].map(([label, value]) => (
                  <Grid item xs={12} sm={6} md={4} key={String(label)}>
                    <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 600 }}>{label}</Typography>
                    <Typography sx={{ fontWeight: 600, color: NJIM.textPrimary }}>{value}</Typography>
                  </Grid>
                ))}
              </Grid>

              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(34,197,94,0.08)",
                      border: "1px solid rgba(34,197,94,0.18)",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <TrendingUpIcon sx={{ color: NJIM.success, fontSize: 18 }} />
                      <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 600 }}>
                        {t("salesRepEnrolment.collected", { defaultValue: "COD Collected" })}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 800, color: NJIM.success, fontSize: "1.1rem" }}>
                      {formatCodAmount(profile.codSummary.collectedAmount, profile.codSummary.currency)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: "rgba(245,158,11,0.08)",
                      border: "1px solid rgba(245,158,11,0.22)",
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                      <ReportProblemOutlinedIcon sx={{ color: NJIM.warning, fontSize: 18 }} />
                      <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, fontWeight: 600 }}>
                        {t("salesRepEnrolment.pending", { defaultValue: "COD Pending" })}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 800, color: NJIM.warning, fontSize: "1.1rem" }}>
                      {formatCodAmount(profile.codSummary.pendingAmount, profile.codSummary.currency)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              {renderCustomerTable(
                profile.customersRep1,
                t("salesRepEnrolment.rep1Customers", { defaultValue: "Customers — Sales Rep (1)" }),
                t("salesRepEnrolment.rep1Hint", {
                  defaultValue: "Primary rep — this rep can place COD orders for these customers.",
                })
              )}
              {renderCustomerTable(
                profile.customersRep2,
                t("salesRepEnrolment.rep2Customers", { defaultValue: "Customers — Sales Rep (2)" }),
                t("salesRepEnrolment.rep2Hint", {
                  defaultValue: "Secondary rep — this rep can also place COD orders for these customers.",
                })
              )}
            </Card>

            <Card
              sx={{
                ...glassCardSx,
                border: "1px solid rgba(245,158,11,0.22)",
                background: "linear-gradient(135deg, rgba(245,158,11,0.06), rgba(255,255,255,0.66))",
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2}>
                <LockResetIcon sx={{ color: NJIM.warning }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {t("salesRepEnrolment.resetPassword", { defaultValue: "Reset Password" })}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary }}>
                    {t("salesRepEnrolment.resetPasswordDesc", {
                      defaultValue: "Set a temporary password. Rep must change it on next login.",
                    })}
                  </Typography>
                </Box>
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("salesRepEnrolment.loginUsername", { defaultValue: "Portal login username" })}
                    value={resetLoginUsername || portalUsername}
                    onChange={(e) => setResetLoginUsername(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type={showResetPw ? "text" : "password"}
                    label={t("salesRepEnrolment.newPassword", { defaultValue: "New Password" })}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowResetPw((p) => !p)}>
                            {showResetPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type={showResetPwConfirm ? "text" : "password"}
                    label={t("salesRepEnrolment.confirmNewPassword", { defaultValue: "Confirm Password" })}
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowResetPwConfirm((p) => !p)}>
                            {showResetPwConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleResetPassword}
                    disabled={!canResetPassword}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${NJIM.warning}, ${NJIM.warning})`,
                    }}
                  >
                    {resetLoading ? (
                      <CircularProgress size={18} sx={{ color: NJIM.white }} />
                    ) : (
                      t("salesRepEnrolment.resetBtn", { defaultValue: "Reset" })
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Card>

            <Card sx={glassCardSx}>
              <Button
                onClick={() => setShowManualRegister((v) => !v)}
                sx={{ textTransform: "none", fontWeight: 700, color: NJIM.ink, mb: showManualRegister ? 2 : 0 }}
              >
                {showManualRegister
                  ? t("salesRepEnrolment.hideManualRegister", { defaultValue: "Hide custom username registration" })
                  : t("salesRepEnrolment.showManualRegister", {
                      defaultValue: "Advanced: register with a custom username",
                    })}
              </Button>
              <Collapse in={showManualRegister}>
                <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary, mb: 2 }}>
                  {t("salesRepEnrolment.manualRegisterHint", {
                    defaultValue:
                      "Sync uses the rep code as username (e.g. 1028). Use this only if you need a different login name.",
                  })}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth size="small" label={t("salesRepEnrolment.username")} value={userName} onChange={(e) => setUserName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label={t("salesRepEnrolment.firstName")} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label={t("salesRepEnrolment.lastName")} value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type={showPassword ? "text" : "password"}
                      label={t("salesRepEnrolment.password")}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPassword((p) => !p)}>
                              {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type={showPasswordConfirm ? "text" : "password"}
                      label={t("salesRepEnrolment.confirmPassword")}
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton size="small" onClick={() => setShowPasswordConfirm((p) => !p)}>
                              {showPasswordConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Button
                  variant="outlined"
                  onClick={handleRegister}
                  disabled={!canRegister}
                  sx={{ mt: 2, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                >
                  {registerLoading ? <CircularProgress size={18} /> : t("salesRepEnrolment.createAccountBtn")}
                </Button>
              </Collapse>
            </Card>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SalesRepEnrolment;
